/**
 * FleetHealthPage — aggregate dashboard at /admin/fleet.
 *
 * Three sections:
 *   - Tile row with RED / AMBER / GREEN counts (color-coded).
 *   - Top-reasons table for non-green devices, sorted desc by count.
 *   - controlProvider breakdown — sanity check for ops who want to know
 *     "are all my devices on the expected adapter?"
 *
 * Refetches every 30 s so an operator watching the page sees their
 * settings flip propagate. No pagination — the endpoint already
 * dealer-scopes via the JWT, so the response is at most one country's
 * worth of devices.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { api } from "../lib/api.js";

interface FleetHealthResult {
  readonly total: number;
  readonly byTraffic: { readonly red: number; readonly amber: number; readonly green: number };
  readonly topReasons: readonly { readonly reason: string; readonly count: number }[];
  readonly byControlProvider: Readonly<Record<string, number>>;
  readonly computedAt: string;
}

interface FleetVersionBreakdown {
  readonly total: number;
  readonly notReported: number;
  readonly rows: readonly {
    readonly versionName: string | null;
    readonly versionCode: number | null;
    readonly count: number;
    readonly activeCount: number;
  }[];
  readonly computedAt: string;
}

export function FleetHealthPage(): JSX.Element {
  const fleet = useQuery({
    queryKey: ["fleet", "health"],
    queryFn: () => api.get<FleetHealthResult>("/api/fleet/health"),
    refetchInterval: 30_000,
  });
  const versions = useQuery({
    queryKey: ["fleet", "version-breakdown"],
    queryFn: () => api.get<FleetVersionBreakdown>("/api/fleet/version-breakdown"),
    refetchInterval: 30_000,
  });

  if (fleet.isLoading) {
    return <p className="text-sm text-slate-500">Loading fleet health…</p>;
  }
  if (fleet.isError || !fleet.data) {
    return <p className="text-sm text-red-700">Failed to load fleet health.</p>;
  }
  const d = fleet.data;
  const greenPct = d.total > 0 ? Math.round((d.byTraffic.green / d.total) * 100) : 0;
  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Fleet health</h1>
        <p className="text-xs text-slate-500">
          {d.total} {d.total === 1 ? "device" : "devices"} · computed{" "}
          {new Date(d.computedAt).toLocaleTimeString()}
        </p>
      </div>

      {/* Traffic-light tile row. Click each to filter devices list. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <TrafficTile
          label="Green"
          count={d.byTraffic.green}
          total={d.total}
          tone="green"
          // The DevicesPage filter wiring (if any) is out of scope for
          // this v0; clicking just navigates to the list for now.
          to="/devices"
        />
        <TrafficTile
          label="Amber"
          count={d.byTraffic.amber}
          total={d.total}
          tone="amber"
          to="/devices"
        />
        <TrafficTile label="Red" count={d.byTraffic.red} total={d.total} tone="red" to="/devices" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Headline</CardTitle>
        </CardHeader>
        <CardBody className="text-sm">
          {d.total === 0 ? (
            <p className="text-slate-500">No devices in this country yet.</p>
          ) : (
            <p>
              <span className="text-2xl font-semibold text-emerald-700">{greenPct}%</span>{" "}
              <span className="text-slate-600">of devices are GREEN.</span>
            </p>
          )}
        </CardBody>
      </Card>

      {d.topReasons.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Top reasons (non-green devices)</CardTitle>
          </CardHeader>
          <CardBody>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="pb-2 font-medium">Reason</th>
                  <th className="pb-2 text-right font-medium">Devices</th>
                </tr>
              </thead>
              <tbody>
                {d.topReasons.map((r) => (
                  <tr key={r.reason} className="border-t border-slate-100">
                    <td className="py-1 pr-4 font-mono text-xs">{r.reason}</td>
                    <td className="py-1 text-right font-mono">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      ) : null}

      {Object.keys(d.byControlProvider).length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Control provider</CardTitle>
          </CardHeader>
          <CardBody>
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(d.byControlProvider).map(([provider, count]) => (
                  <tr key={provider} className="border-t border-slate-100">
                    <td className="py-1 pr-4 font-mono">
                      <Link
                        to="/devices"
                        search={{ provider }}
                        className="text-brand-600 hover:underline"
                      >
                        {provider}
                      </Link>
                    </td>
                    <td className="py-1 text-right font-mono">{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      ) : null}

      {versions.data && versions.data.rows.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>DPC version breakdown</CardTitle>
            <span className="text-xs text-slate-500">
              live = synced recently · {versions.data.notReported} not reported
            </span>
          </CardHeader>
          <CardBody>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-1 pr-4 font-medium">Version</th>
                  <th className="py-1 pr-4 text-right font-medium">Devices</th>
                  <th className="py-1 text-right font-medium">Live</th>
                </tr>
              </thead>
              <tbody>
                {versions.data.rows.map((r) => {
                  const label =
                    r.versionName != null
                      ? `${r.versionName} (code ${r.versionCode ?? "?"})`
                      : "Not reported";
                  const search =
                    r.versionCode != null
                      ? { versionCode: String(r.versionCode) }
                      : { notReported: "1" };
                  return (
                    <tr key={`${r.versionCode ?? "null"}`} className="border-t border-slate-100">
                      <td className="py-1 pr-4 font-mono">
                        <Link
                          to="/devices"
                          search={search}
                          className="text-brand-600 hover:underline"
                        >
                          {label}
                        </Link>
                      </td>
                      <td className="py-1 pr-4 text-right font-mono">{r.count}</td>
                      <td className="py-1 text-right font-mono text-emerald-700">
                        {r.versionName != null ? r.activeCount : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

function TrafficTile({
  label,
  count,
  total,
  tone,
  to,
}: {
  label: string;
  count: number;
  total: number;
  tone: "green" | "amber" | "red";
  to: "/devices";
}): JSX.Element {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const toneClasses = {
    green: "border-emerald-200 bg-emerald-50",
    amber: "border-amber-200 bg-amber-50",
    red: "border-rose-200 bg-rose-50",
  }[tone];
  const textTone = {
    green: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-rose-700",
  }[tone];
  return (
    <Link to={to}>
      <div className={`rounded-lg border ${toneClasses} p-4 transition hover:opacity-90`}>
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`mt-2 text-3xl font-semibold ${textTone}`}>{count}</div>
        <div className="mt-1 text-xs text-slate-500">{pct}% of fleet</div>
      </div>
    </Link>
  );
}
